import { View, StyleSheet, Pressable, Platform, useWindowDimensions } from 'react-native'
import { DS } from '../../../design-system'
import { useCallback, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Doc } from '@ppwr/convex/_generated/dataModel'
import { MaterialIcons } from '@expo/vector-icons'
import Svg, { Path, Circle, Line, Text as SvgText } from 'react-native-svg'
import { line, curveMonotoneX } from 'd3-shape'
import { scaleLinear, scalePoint } from 'd3-scale'
import { max } from 'd3-array'

const RANGE_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 }
] as const

// Derived from the schema's gameModeValidator — adding a mode there makes
// missing entries in these maps a compile error.
type Mode = Doc<'games'>['mode']

const MODE_COLORS: Record<Mode, string> = {
  practice: '#7C3AED',
  private: '#2563EB',
  public: '#16A34A',
  community: '#EA580C',
  daily10: '#0891B2',
  mtt: '#DB2777'
}

const MODE_LABELS: Record<Mode, string> = {
  practice: 'Practice',
  private: 'Private',
  public: 'Public',
  community: 'Community',
  daily10: 'Daily 10',
  mtt: 'MTT'
}

const MODES = Object.keys(MODE_COLORS) as Mode[]

type DayData = {
  date: string
  [key: string]: { games: number; hands: number } | string
}

const CHART_HEIGHT = 220
const Y_AXIS_WIDTH = 36
const X_AXIS_HEIGHT = 32
const DOT_RADIUS = 4
const TICK_COUNT = 5

type LineChartProps = {
  data: DayData[]
  metric: 'games' | 'hands'
}

const getModeValue = (row: DayData, mode: Mode, metric: 'games' | 'hands') => {
  const entry = row[mode] as { games: number; hands: number } | undefined
  return entry?.[metric] ?? 0
}

const getDayTotal = (row: DayData, metric: 'games' | 'hands') =>
  MODES.reduce((sum, m) => sum + getModeValue(row, m, metric), 0)

type TooltipData = { x: number; y: number; row: DayData }

const Tooltip = ({
  tooltipData,
  metric,
  chartWidth
}: {
  tooltipData: TooltipData
  metric: 'games' | 'hands'
  chartWidth: number
}) => {
  const TOOLTIP_WIDTH = 148
  // Estimated tooltip height to position it above the dot
  const TOOLTIP_HEIGHT = 100

  // Keep tooltip inside the chart horizontally
  let left = tooltipData.x - TOOLTIP_WIDTH / 2
  if (left < 0) left = 0
  if (left + TOOLTIP_WIDTH > chartWidth) left = chartWidth - TOOLTIP_WIDTH

  const top = Math.max(0, tooltipData.y - TOOLTIP_HEIGHT - 8)

  return (
    <View style={[styles.tooltip, { left, top }]}>
      <DS.Text variant="Body Small/Regular" style={styles.tooltipDate}>
        {tooltipData.row.date as string}
      </DS.Text>
      {MODES.map((mode) => {
        const val = getModeValue(tooltipData.row, mode, metric)
        if (val === 0) return null
        return (
          <View key={mode} style={styles.tooltipRow}>
            <View style={[styles.legendDot, { backgroundColor: MODE_COLORS[mode] }]} />
            <DS.Text variant="Body Small/Regular">
              {MODE_LABELS[mode]}: {val}
            </DS.Text>
          </View>
        )
      })}
      <DS.Text variant="Body Small/Regular" style={styles.tooltipTotal}>
        Total: {getDayTotal(tooltipData.row, metric)}
      </DS.Text>
    </View>
  )
}

const LineChart = ({ data, metric }: LineChartProps) => {
  const { width: windowWidth } = useWindowDimensions()
  const [width, setWidth] = useState(windowWidth)
  const [tooltip, setTooltip] = useState<TooltipData | null>(null)

  if (data.length === 0) {
    return (
      <View style={styles.empty}>
        <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
          No data for this period
        </DS.Text>
      </View>
    )
  }

  const svgWidth = width - Y_AXIS_WIDTH
  const svgHeight = CHART_HEIGHT + X_AXIS_HEIGHT

  const dates = data.map((d) => d.date)

  const allValues = MODES.flatMap((mode) => data.map((row) => getModeValue(row, mode, metric)))
  const yMax = max(allValues) ?? 1

  const xScale = scalePoint<string>().domain(dates).range([0, svgWidth]).padding(0.5)
  const yScale = scaleLinear().domain([0, yMax]).range([CHART_HEIGHT, 0]).nice()

  const ticks = yScale.ticks(TICK_COUNT)

  // Label skip factor so x-axis labels don't overlap
  const labelSkip = data.length > 30 ? 7 : data.length > 14 ? 3 : 1

  return (
    <View
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
      style={styles.chartContainer}
    >
      {width > 0 && (
        <View style={{ flexDirection: 'row' }}>
          {/* Y-axis labels */}
          <View style={{ width: Y_AXIS_WIDTH, height: svgHeight, position: 'relative' }}>
            {ticks.map((tick) => (
              <DS.Text
                key={tick}
                variant="Body Small/Regular"
                style={[
                  styles.yLabel,
                  { top: yScale(tick) - 7 }
                ]}
              >
                {tick}
              </DS.Text>
            ))}
          </View>

          {/* Chart SVG */}
          <View style={{ flex: 1, position: 'relative' }}>
            <Svg width={svgWidth} height={svgHeight}>
              {/* Horizontal gridlines */}
              {ticks.map((tick) => (
                <Line
                  key={tick}
                  x1={0}
                  x2={svgWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke="#e5e7eb"
                  strokeWidth={1}
                  strokeDasharray="4,3"
                />
              ))}

              {/* Lines per mode */}
              {MODES.map((mode) => {
                const pathData = line<DayData>()
                  .x((d) => xScale(d.date) ?? 0)
                  .y((d) => yScale(getModeValue(d, mode, metric)))
                  .curve(curveMonotoneX)(data)

                return pathData ? (
                  <Path
                    key={mode}
                    d={pathData}
                    stroke={MODE_COLORS[mode]}
                    strokeWidth={2}
                    fill="none"
                  />
                ) : null
              })}

              {/* Dots per data point per mode */}
              {MODES.map((mode) =>
                data.map((row) => {
                  const cx = xScale(row.date) ?? 0
                  const cy = yScale(getModeValue(row, mode, metric))
                  const isActive =
                    tooltip?.row.date === row.date

                  return (
                    <Circle
                      key={`${mode}-${row.date}`}
                      cx={cx}
                      cy={cy}
                      r={isActive ? DOT_RADIUS + 1 : DOT_RADIUS}
                      fill={MODE_COLORS[mode]}
                      stroke="#fff"
                      strokeWidth={1.5}
                      onPress={() => {
                        if (tooltip?.row.date === row.date) {
                          setTooltip(null)
                        } else {
                          setTooltip({ x: cx, y: cy, row })
                        }
                      }}
                      // Web hover
                      {...(Platform.OS === 'web'
                        ? {
                            onMouseEnter: () => setTooltip({ x: cx, y: cy, row }),
                            onMouseLeave: () => setTooltip(null)
                          }
                        : {})}
                    />
                  )
                })
              )}

              {/* X-axis date labels */}
              {dates.map((date, i) => {
                if (i % labelSkip !== 0) return null
                return (
                  <SvgText
                    key={date}
                    x={xScale(date) ?? 0}
                    y={CHART_HEIGHT + 16}
                    fontSize={9}
                    fill="#666"
                    textAnchor="middle"
                  >
                    {date.slice(5)}
                  </SvgText>
                )
              })}
            </Svg>

            {/* Tooltip overlay */}
            {tooltip && (
              <Tooltip
                tooltipData={tooltip}
                metric={metric}
                chartWidth={svgWidth}
              />
            )}
          </View>
        </View>
      )}
    </View>
  )
}

const PeriodTotals = ({ data, metric }: LineChartProps) => {
  const grandTotal = data.reduce((sum, row) => sum + getDayTotal(row, metric), 0)

  return (
    <View style={styles.periodTotals}>
      {MODES.map((mode) => {
        const total = data.reduce((sum, row) => sum + getModeValue(row, mode, metric), 0)
        return (
          <View key={mode} style={styles.periodTotalItem}>
            <View style={[styles.legendDot, { backgroundColor: MODE_COLORS[mode] }]} />
            <DS.Text variant="Body Small/Regular">
              {MODE_LABELS[mode]}: <DS.Text variant="Body Small/Regular" style={{ fontWeight: '600' }}>{total}</DS.Text>
            </DS.Text>
          </View>
        )
      })}
      <View style={styles.periodTotalItem}>
        <DS.Text variant="Body Small/Regular" style={{ fontWeight: '700' }}>
          Total: {grandTotal}
        </DS.Text>
      </View>
    </View>
  )
}

const Legend = () => (
  <View style={styles.legend}>
    {MODES.map((mode) => (
      <View key={mode} style={styles.legendItem}>
        <View style={[styles.legendDot, { backgroundColor: MODE_COLORS[mode] }]} />
        <DS.Text variant="Body Small/Regular">{MODE_LABELS[mode]}</DS.Text>
      </View>
    ))}
  </View>
)

const exportCsv = (data: DayData[], days: number) => {
  const header = [
    'Date',
    'Practice Games', 'Private Games', 'Public Games', 'Community Games', 'Daily10 Games', 'MTT Games', 'Total Games',
    'Practice Hands', 'Private Hands', 'Public Hands', 'Community Hands', 'Daily10 Hands', 'MTT Hands', 'Total Hands'
  ].join(',')

  const rows = data.map((row) => {
    const gamesPerMode = MODES.map((m) => getModeValue(row, m, 'games'))
    const handsPerMode = MODES.map((m) => getModeValue(row, m, 'hands'))
    const totalGames = gamesPerMode.reduce((a, b) => a + b, 0)
    const totalHands = handsPerMode.reduce((a, b) => a + b, 0)
    return [row.date, ...gamesPerMode, totalGames, ...handsPerMode, totalHands].join(',')
  })

  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `game-report-${days}d.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export const DailyReportChart = () => {
  const [days, setDays] = useState<7 | 30 | 90>(30)
  const data = useQuery(api.reports.queries.getDailyGameReport, { days })

  const handleExport = useCallback(() => {
    if (data && data.length > 0) exportCsv(data as DayData[], days)
  }, [data, days])

  return (
    <View style={styles.container}>
      {/* Range selector + export */}
      <View style={styles.topRow}>
        <View style={styles.rangeRow}>
          {RANGE_OPTIONS.map((opt) => (
            <Pressable
              key={opt.days}
              onPress={() => setDays(opt.days)}
              style={[styles.rangeBtn, days === opt.days && styles.rangeBtnActive]}
            >
              <DS.Text variant="Body Small/Regular" color={days === opt.days ? 'Global/White' : 'Brand/Dark Grey'}>
                {opt.label}
              </DS.Text>
            </Pressable>
          ))}
        </View>
        <Pressable onPress={handleExport} style={styles.exportBtn}>
          <MaterialIcons name="file-download" size={16} color={DS.palette['Brand/Purple']} />
          <DS.Text variant="Body Small/Regular" color="Brand/Purple">
            Export CSV
          </DS.Text>
        </Pressable>
      </View>

      <View style={styles.legendRow}>
        <Legend />
        <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
          Times in UTC
        </DS.Text>
      </View>

      {/* Games played */}
      <View style={styles.section}>
        <DS.Text variant="Heading 3/Regular">Games Played by Type</DS.Text>
        <LineChart data={(data as DayData[]) ?? []} metric="games" />
        <PeriodTotals data={(data as DayData[]) ?? []} metric="games" />
      </View>

      {/* Hands played */}
      <View style={styles.section}>
        <DS.Text variant="Heading 3/Regular">Hands Played by Type</DS.Text>
        <LineChart data={(data as DayData[]) ?? []} metric="hands" />
        <PeriodTotals data={(data as DayData[]) ?? []} metric="hands" />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: { gap: 24 },
  topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  rangeRow: { flexDirection: 'row', gap: 8 },
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Purple'],
    backgroundColor: DS.palette['Global/White']
  },
  rangeBtn: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    backgroundColor: DS.palette['Global/White']
  },
  rangeBtnActive: {
    backgroundColor: DS.palette['Brand/Purple'],
    borderColor: DS.palette['Brand/Purple']
  },
  legendRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  section: { gap: 12 },
  chartContainer: {
    minHeight: CHART_HEIGHT + X_AXIS_HEIGHT,
    paddingTop: 8
  },
  yLabel: {
    position: 'absolute',
    right: 4,
    fontSize: 9,
    color: '#666',
    textAlign: 'right'
  },
  tooltip: {
    position: 'absolute',
    zIndex: 10,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 3,
    width: 148,
    // @ts-ignore — web shadow
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  tooltipDate: { color: '#fff', fontWeight: '600', marginBottom: 2 },
  tooltipRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  tooltipTotal: { color: '#ccc', marginTop: 2, fontWeight: '600' },
  periodTotals: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: DS.palette['Brand/Light Grey']
  },
  periodTotalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6
  },
  empty: { paddingVertical: 24, alignItems: 'center' }
})
